import { merge, from, Observable } from 'rxjs';
import { map, startWith, concatMap } from 'rxjs/operators';
import { fromPortableEvent, filterNulls } from '../util';
import { VisualElementControllerLoader } from './LoaderFactory';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { getTitleObservables } from '../util/birdRichText';
import { DataStateChangeEvent, VisualElementController } from '@sas-dvr/ltjs-bird';
import { BusyState } from '@sas-dvr/internal-va-react-core/store/busy/BusyState';
import { UnknownState } from '@sas-dvr/internal-va-react-core/store/birdElement/unknownState';
import { AnyAction } from 'redux';
import {
  AboutInfoState,
  AboutInfoData,
} from '@sas-dvr/internal-va-react-core/store/aboutInfo/AboutInfoState';
import { SelectionDisabledState } from '@sas-dvr/internal-va-react-core/store/selectiondisabled/SelectionDisabledState';
import { AccessibilityLabelState } from '@sas-dvr/internal-va-react-core/store/accessibilityLabel/accessibilityLabelState';

export const loadVisualElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  _myActions$,
  state$,
  deps,
  lifecyleActions
) => {
  const id = deps.getKey(visualElementController);

  lifecyleActions = lifecyleActions ?? {
    create: () => UnknownState.actions.add(id, {}),
    delete: () => UnknownState.actions.delete(id),
  };

  const observables: Observable<AnyAction>[] = getTitleObservables(
    id,
    visualController,
    visualElementController,
    state$
  );

  observables.push(
    fromPortableEvent(visualElementController, VisualElementController.BUSY).pipe(
      map(() => BusyState.actions.setBusy(id, { isBusy: true }))
    )
  );

  observables.push(
    fromPortableEvent(visualElementController, VisualElementController.TRANSITION_STARTED).pipe(
      map(() => BusyState.actions.hideIndicator(id))
    )
  );

  observables.push(
    fromPortableEvent(visualElementController, VisualElementController.READY).pipe(
      map(() => BusyState.actions.setBusy(id, { isBusy: false }))
    )
  );

  observables.push(
    fromPortableEvent(visualElementController, DataStateChangeEvent.DATACHANGE).pipe(
      startWith(null), // Emit the following actions once before the first DATACHANGE
      concatMap(() => {
        const actions: AnyAction[] = [];

        const visualElementModel = visualElementController.getVisualElementModel();
        const selectionDisabled = visualElementModel?.getSelectionDisabled() ?? false;
        actions.push(SelectionDisabledState.actions.set(id, selectionDisabled));

        const aboutInfoValue = getCurrentAboutInfoValue(visualElementController);
        actions.push(AboutInfoState.actions.setValue(id, aboutInfoValue));

        const accessibilityLabel = visualElementController.getAccessibilityLabel();
        actions.push(AccessibilityLabelState.actions.set(id, accessibilityLabel ?? undefined));

        return from(actions);
      })
    )
  );

  //
  // Add the visual element data. If no createElement function is provided then default to the 'unknown' BirdElement.
  //
  // Use update here rather than set because loadPromptHeader sets prompt layout constraints
  const layoutDataAction = BirdLayoutState.actions.update(
    id,
    visualController ? deps.getLayoutData(visualController) : {}
  );
  const busyAction = BusyState.actions.setBusy(id, { isBusy: visualElementController.isBusy() });

  if (lifecyleActions.delete) {
    observables.push(unload$.pipe(map(lifecyleActions.delete)));
  }
  return merge(from([layoutDataAction, lifecyleActions.create(), busyAction]), ...observables);
};

function getCurrentAboutInfoValue(
  visualElementController: VisualElementController
): AboutInfoData['value'] {
  const aboutInfo = visualElementController.getAboutInfo();
  if (!aboutInfo) {
    return;
  }
  const label = aboutInfo.getLabel();
  const messages = filterNulls(aboutInfo.getMessages());
  if (!label || !messages.length) {
    return;
  }
  return { label, messages };
}
